import { useCallback, useMemo, useState } from 'react';

type UseWhitespaceAxesControlsParams = {
    xAxis: string;
    yAxis: string;
    crossSection: string;
    setXAxis: (value: string) => void;
    setYAxis: (value: string) => void;
    setCrossSection: (value: string) => void;
    markConfigRefreshStart: () => void;
    trackAxesMenuOpened: () => void;
    trackXAxisSelected: (previousXAxis: string, selectedXAxis: string) => void;
    trackYAxisSelected: (previousYAxis: string, selectedYAxis: string) => void;
    trackCrossSectionOpened: () => void;
    trackCrossSectionSelected: (previousCrossSection: string, selectedCrossSection: string) => void;
};

const useWhitespaceAxesControls = ({
    xAxis,
    yAxis,
    crossSection,
    setXAxis,
    setYAxis,
    setCrossSection,
    markConfigRefreshStart,
    trackAxesMenuOpened,
    trackXAxisSelected,
    trackYAxisSelected,
    trackCrossSectionOpened,
    trackCrossSectionSelected,
}: UseWhitespaceAxesControlsParams) => {
    const [pendingXAxis, setPendingXAxis] = useState(xAxis);
    const [pendingYAxis, setPendingYAxis] = useState(yAxis);
    const [isAxesPopoverOpen, setIsAxesPopoverOpen] = useState(false);
    const [isCrossSectionOpen, setIsCrossSectionOpen] = useState(false);

    const handleAxesPopoverOpenChange = useCallback(
        (open: boolean) => {
            setIsAxesPopoverOpen(open);
            if (open) {
                setPendingXAxis(xAxis);
                setPendingYAxis(yAxis);
                trackAxesMenuOpened();
            }
        },
        [trackAxesMenuOpened, xAxis, yAxis],
    );

    const handlePendingXAxisChange = useCallback(
        (nextValue: string) => {
            trackXAxisSelected(pendingXAxis, nextValue);
            setPendingXAxis(nextValue);
        },
        [pendingXAxis, trackXAxisSelected],
    );

    const handlePendingYAxisChange = useCallback(
        (nextValue: string) => {
            trackYAxisSelected(pendingYAxis, nextValue);
            setPendingYAxis(nextValue);
        },
        [pendingYAxis, trackYAxisSelected],
    );

    const handleApplyAxes = useCallback(() => {
        markConfigRefreshStart();
        setXAxis(pendingXAxis);
        setYAxis(pendingYAxis);
        setIsAxesPopoverOpen(false);
    }, [markConfigRefreshStart, pendingXAxis, pendingYAxis, setXAxis, setYAxis]);

    const handleCrossSectionOpenChange = useCallback(
        (open: boolean) => {
            setIsCrossSectionOpen(open);
            if (open) {
                trackCrossSectionOpened();
            }
        },
        [trackCrossSectionOpened],
    );

    const handleCrossSectionSelect = useCallback(
        (option: string) => {
            trackCrossSectionSelected(crossSection, option);
            markConfigRefreshStart();
            setCrossSection(option);
            setIsCrossSectionOpen(false);
        },
        [crossSection, markConfigRefreshStart, setCrossSection, trackCrossSectionSelected],
    );

    const axesControlState = useMemo(
        () => ({
            pendingXAxis,
            pendingYAxis,
            isAxesPopoverOpen,
            isCrossSectionOpen,
            handleAxesPopoverOpenChange,
            handlePendingXAxisChange,
            handlePendingYAxisChange,
            handleApplyAxes,
            handleCrossSectionOpenChange,
            handleCrossSectionSelect,
        }),
        [
            handleApplyAxes,
            handleAxesPopoverOpenChange,
            handleCrossSectionOpenChange,
            handleCrossSectionSelect,
            handlePendingXAxisChange,
            handlePendingYAxisChange,
            isAxesPopoverOpen,
            isCrossSectionOpen,
            pendingXAxis,
            pendingYAxis,
        ],
    );

    return axesControlState;
};

export default useWhitespaceAxesControls;
